import { faEdit } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../../Authentication/Authorization'
import DialogContext from '../../../Authentication/DialogContext'
import DataTable from '../../../components/DataTable'
import { EditDialogProps } from '../../../components/EditDialog'
import ProtectedButton from '../../../components/ProtectedButton'
import SsoProxyService from '../../../service/SsoProxyService'
import { formatBooleanColumn } from '../../../utils/DatatableUtil'

const GatewayIssuerConfig = (props: any) => {
  const { t } = useTranslation()
  const { configurationStatus, setConfigurationStatus } = props
  const [gatewayIssuerConfig, setGatewayIssuerConfig] = useState({} as any)
  const [signatureAlgorithms, setSignatureAlgorithms] = useState([] as any[])
  const { editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    async function loadData() {
      const config = await SsoProxyService.getGatewayIssuerConfiguration()
      setGatewayIssuerConfig(config)
      const signatureAlgorithms = await SsoProxyService.getSupportedSignatureAlgorithms()
      setSignatureAlgorithms(signatureAlgorithms)
    }
    loadData()
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        formatter: (cell: any, row: any) => {
          if (row.type === 'BOOLEAN') {
            return formatBooleanColumn(cell)
          }
          return cell
        },
        sort: true,
      },
    ]
  }, [])

  const data = getData()

  return (
    <>
      <DataTable keyField='label' data={data} columns={columns} headerClasses='d-none' searchable={false} />
      <div className='text-right pr-1 ml-nfc-1'>
        <ProtectedButton variant='primary' className='ml-auto my-auto' onClick={editGatewayIssuerConfig} permissions={Authorization.settings.ssoProxy.edit}>
          <FontAwesomeIcon icon={faEdit} />
          <span className='ml-1 my-auto'>Edit</span>
        </ProtectedButton>
      </div>
    </>
  )

  function getData() {
    return configurationStatus !== undefined && configurationStatus.ssoHostNameSet === true
      ? [
          {
            label: t('ssoProxy.ssoProxyHostname'),
            value: gatewayIssuerConfig.ssoProxyHostname,
            type: 'STRING',
          },
          {
            label: t('ssoProxy.signatureAlgorithm'),
            value: gatewayIssuerConfig.signatureAlgorithm?.displayName,
            type: 'STRING',
          },
          {
            label: t('ssoProxy.signAuthNRequests'),
            value: gatewayIssuerConfig.signAuthNRequests,
            type: 'BOOLEAN',
          },
          {
            label: t('ssoProxy.wantAssertionsSigned'),
            value: gatewayIssuerConfig.wantAssertionsSigned,
            type: 'BOOLEAN',
          },
          {
            label: t('ssoProxy.tokenizeSubjectNameId'),
            value: gatewayIssuerConfig.tokenizeSubjectNameId,
            type: 'BOOLEAN',
          },
        ]
      : []
  }

  function editGatewayIssuerConfig() {
    const props: EditDialogProps = {
      show: true,
      header: t('ssoProxy.issuerConfig'),
      labelGridSize: 7,
      onSubmit: SsoProxyService.setGatewayIssuerConfiguration,
      onSubmitExtraArgs: [t, setGatewayIssuerConfig, setConfigurationStatus],
      data: [
        {
          id: 'ssoProxyHostname',
          label: t('ssoProxy.ssoProxyHostname'),
          value: gatewayIssuerConfig.ssoProxyHostname,
          type: 'text',
          placeholder: t('ssoProxy.ssoProxyHostname'),
          autoComplete: 'ssoProxyHostname',
        },
        {
          id: 'signatureAlgorithm',
          type: 'select',
          options: signatureAlgorithms.map((sa: any) => ({ label: sa.displayName, value: sa })),
          label: t('ssoProxy.signatureAlgorithm'),
          value: gatewayIssuerConfig.signatureAlgorithm,
          required: true,
        },
        {
          groupLabel: t('ssoProxy.signAuthNRequests'),
          groupName: 'signAuthNRequests',
          type: 'switch',
          checkboxes: [
            {
              id: 'signAuthNRequests',
              value: gatewayIssuerConfig.signAuthNRequests,
            },
          ],
        },
        {
          groupLabel: t('ssoProxy.wantAssertionsSigned'),
          groupName: 'wantAssertionsSigned',
          type: 'switch',
          checkboxes: [
            {
              id: 'wantAssertionsSigned',
              value: gatewayIssuerConfig.wantAssertionsSigned,
            },
          ],
        },
        {
          groupLabel: t('ssoProxy.tokenizeSubjectNameId'),
          groupName: 'tokenizeSubjectNameId',
          type: 'switch',
          checkboxes: [
            {
              id: 'tokenizeSubjectNameId',
              value: gatewayIssuerConfig.tokenizeSubjectNameId,
            },
          ],
        },
      ],
    }

    editDialogPropsSetter(props)
  }
}

export default GatewayIssuerConfig
